import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Dialog,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SmartDisplayOutlinedIcon from "@mui/icons-material/SmartDisplayOutlined";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MainHeading from "../components/main-heading.component";
import { _delete, _get, _post } from "../services/api.service";
import { getContentFormatIcon } from "../utils/contentFormatIcons";
import { contentEngineService } from "./content-engine-v2/content-engine.service";
import "./content-engine-v2/content-engine.css";
import "./content-engine-v2/studio-dialog.css";
import "./video-create/CreateVideo.css";
import "./video-projects/VideoProjects.css";
import "./library/Library.css";
import "./library/TopicContents.css";
import "./content-engine-v2/script-detail/ScriptDetail.css";

interface Block {
  id: number;
  order: number;
  block_type: string;
  block_type_label: string;
  title: string;
  narration: string;
  caption: string;
  payload: Record<string, unknown>;
  metadata: Record<string, unknown>;
  estimated_duration: number | string | null;
}

interface Version {
  id: number;
  version: number;
  blocks: Block[];
  created_at: string;
  updated_at: string;
}

interface ContentItem {
  id: number;
  content_format: {
    id: number;
    name: string;
    slug: string;
  } | null;
  tag_display: string;
  pattern_key: string;
  latest_version: Version | null;
  versions: Version[];
  script_selected: boolean;
  video_created: boolean;
  created_at: string;
  updated_at: string;
}

interface TopicDetail {
  id: number;
  title: string;
  language: string;
  source: string;
  created_at: string;
  updated_at: string;
  contents: ContentItem[];
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function formatDuration(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds <= 0) return "0s";
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.round(totalSeconds % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

function PageSkeleton() {
  return (
    <div className="tc-skeleton-list">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="tc-skeleton-card">
          <Skeleton variant="text" width="40%" sx={{ fontSize: 15, mb: 1.5 }} />
          <Skeleton variant="text" width="80%" sx={{ fontSize: 12 }} />
          <Skeleton variant="text" width="60%" sx={{ fontSize: 12 }} />
        </div>
      ))}
    </div>
  );
}

function VersionBlocks({ version, highlighted = false, active = false, showActiveBadge = true }: {
  version: Version;
  highlighted?: boolean;
  active?: boolean;
  showActiveBadge?: boolean;
}) {
  const blocks = version.blocks ?? [];
  const totalSecs = Math.round(
    blocks.reduce((s, b) => s + (parseFloat(String(b.estimated_duration)) || 0), 0)
  );
  const totalWords = blocks.reduce((s, b) => {
    const text = [b.title, b.narration, b.caption].filter(Boolean).join(" ");
    return s + text.split(/\s+/).filter(Boolean).length;
  }, 0);

  return (
    <div className="tc-dialog-blocks">
      <div className="tc-dialog-stats">
        {totalSecs > 0 && (
          <span className="tc-dialog-stat-pill">
            <AccessTimeIcon sx={{ fontSize: 14 }} />
            ~{totalSecs >= 60 ? `${Math.floor(totalSecs / 60)}m ${totalSecs % 60}s` : `${totalSecs}s`}
          </span>
        )}
        {totalWords > 0 && (
          <span className="tc-dialog-stat-pill">
            <TextFieldsIcon sx={{ fontSize: 14 }} />
            {totalWords} words
          </span>
        )}
        {blocks.length > 0 && (
          <span className="tc-dialog-stat-pill">
            <ArticleOutlinedIcon sx={{ fontSize: 14 }} />
            {blocks.length} {blocks.length === 1 ? "block" : "blocks"}
          </span>
        )}
        {version.updated_at && (
          <span className="tc-dialog-stat-pill tc-dialog-stat-pill--muted">
            <ScheduleIcon sx={{ fontSize: 14 }} />
            Updated {new Date(version.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </div>
      <div className="tc-dialog-blocks-list">
        {blocks.map((block, blockIndex) => (
          <div key={block.id} className="ce-dialog-block-card">
            <div className="ce-dialog-block-header">
              <span className="ce-dialog-block-label">
                {blockIndex + 1}. {block.block_type_label || block.block_type.replace(/_/g, " ")}
              </span>
            </div>
            <div className="ce-dialog-block-body">
              <div className="sd-block-fields">
                {block.title && (
                  <div className="sd-block-field">
                    <span className="sd-field-badge sd-field-badge-t">T</span>
                    <span className="sd-block-field-title">{block.title}</span>
                  </div>
                )}
                {block.narration && (
                  <div className="sd-block-field">
                    <span className="sd-field-badge sd-field-badge-n">N</span>
                    <span className="sd-block-field-text">{block.narration}</span>
                  </div>
                )}
                {block.caption && (
                  <div className="sd-block-field">
                    <span className="sd-field-badge sd-field-badge-c">C</span>
                    <span className="sd-block-field-caption">{block.caption}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VersionBlock({ version, topicTitle, contentId, contentFormat, active, showActiveBadge = true, showActiveHighlight = true, multipleVersions = false, defaultOpen = false, onActivate, onDelete, onRefine }: {
  version: Version;
  topicTitle: string;
  contentId: number;
  contentFormat: ContentItem["content_format"];
  active: boolean;
  showActiveBadge?: boolean;
  showActiveHighlight?: boolean;
  multipleVersions?: boolean;
  defaultOpen?: boolean;
  onActivate?: (version: Version) => void;
  onDelete?: (version: Version) => void;
  onRefine?: (version: Version) => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(defaultOpen);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const highlighted = active && showActiveHighlight;
  const totalDuration = useMemo(
    () =>
      (version.blocks ?? []).reduce(
        (sum, b) => sum + (b.estimated_duration ? Number(b.estimated_duration) : 0),
        0
      ),
    [version.blocks]
  );

  const handleTurnToVideo = () => {
    const fullText = (version.blocks ?? [])
      .map((b) => [b.title, b.narration, b.caption].filter(Boolean).join("\n\n"))
      .join("\n\n");
    contentEngineService.setCreativeVersionId(version.id);
    contentEngineService.setScriptPreview(fullText);
    if (contentId) {
      _post(`/content-engine/contents/${contentId}/create-video/`, {}, {}, false, true).catch(() => {});
    }
    navigate("/quick-video");
  };

  useEffect(() => {
    if (open) {
      const bodyOverflow = document.body.style.overflow;
      const htmlOverflow = document.documentElement.style.overflow;

      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = bodyOverflow;
        document.documentElement.style.overflow = htmlOverflow;
      };
    }
  }, [open]);

  return (
    <>
      <div
        className={`tc-version-card${highlighted ? " is-highlighted" : ""}`}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="tc-version-row">
          <span className={`tc-chip tc-chip--version${highlighted ? " is-active" : ""}`}>
            v{version.version}
          </span>
          <span className="tc-version-preview">
            {version.blocks?.[0]?.title || "Untitled"}
          </span>
          {active && showActiveBadge && (
            <span className="tc-chip tc-chip--active">Active</span>
          )}
          <span className="tc-version-meta">
            {formatDuration(totalDuration)}
          </span>
          <span className="tc-version-meta">
            <ScheduleIcon sx={{ fontSize: 11 }} />
            Updated {relativeTime(version.updated_at)}
          </span>
          <span className="tc-version-menu">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchor(e.currentTarget);
              }}
              sx={{ p: 0.4, color: "#66738a", "&:hover": { color: "#16213a" } }}
            >
              <MoreVertIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </div>
      </div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              minWidth: 190,
              py: 1,
              mt: 0.75,
              bgcolor: "var(--card)",
              border: "1px solid var(--card-border)",
              boxShadow: "0 12px 40px rgba(15, 23, 42, 0.14), 0 2px 8px rgba(15, 23, 42, 0.06)",
              "& .MuiList-root": { p: 0 },
            },
          },
        }}
      >
        <MenuItem
          onClick={() => { setMenuAnchor(null); setOpen(true); }}
          sx={{
            fontSize: 13,
            fontWeight: 500,
            gap: 1.2,
            py: 1.15,
            px: 2,
            mx: 1,
            my: 0.25,
            borderRadius: 2,
            color: "var(--muted)",
            "&:hover": { bgcolor: "var(--soft)" },
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 16, opacity: 0.7 }} /> Open
        </MenuItem>
        <MenuItem
          onClick={() => { setMenuAnchor(null); onRefine?.(version); }}
          sx={{
            fontSize: 13,
            fontWeight: 500,
            gap: 1.2,
            py: 1.15,
            px: 2,
            mx: 1,
            my: 0.25,
            borderRadius: 2,
            color: "var(--muted)",
            "&:hover": { bgcolor: "rgba(35, 100, 232, 0.08)" },
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 16 }} /> Refine
        </MenuItem>
        {multipleVersions && !active && (
          <MenuItem
            onClick={() => { setMenuAnchor(null); onActivate?.(version); }}
            sx={{
              fontSize: 13,
              fontWeight: 500,
              gap: 1.2,
              py: 1.15,
              px: 2,
              mx: 1,
              my: 0.25,
              borderRadius: 2,
              color: "var(--muted)",
              "&:hover": { bgcolor: "rgba(35, 100, 232, 0.08)" },
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> Mark Active
          </MenuItem>
        )}
        <Box sx={{ height: 1, bgcolor: "var(--card-border)", mx: 2, my: 0.75 }} />
        <MenuItem
          onClick={() => { setMenuAnchor(null); onDelete?.(version); }}
          sx={{
            fontSize: 13,
            fontWeight: 500,
            gap: 1.2,
            py: 1.15,
            px: 2,
            mx: 1,
            my: 0.25,
            borderRadius: 2,
            color: "var(--muted)",
            "&:hover": { bgcolor: "rgba(211, 47, 47, 0.08)" },
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 16 }} /> Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        className="tc-script-dialog ce-studio-dialog"
        slotProps={{
          backdrop: { className: "ce-studio-dialog-backdrop" },
          paper: {
            className: "tc-script-dialog-paper ce-studio-dialog-paper",
            sx: { maxWidth: "920px", width: "100%" },
          },
        }}
      >
        <div className="ce-dialog-header">
          <div className="ce-dialog-header-inner">
            <div className="ce-dialog-header-left">
              <div className="ce-dialog-icon-wrap">
                <ArticleOutlinedIcon sx={{ fontSize: 22 }} />
              </div>
              <div className="tc-dialog-header-copy">
                <h2 className="ce-dialog-title">{capitalize(topicTitle)}</h2>
                <div className="tc-dialog-chip-row">
                  {contentFormat && (
                    <span className="tc-chip tc-chip--format">{contentFormat.name.toUpperCase()}</span>
                  )}
                  <span className={`tc-chip tc-chip--version${highlighted ? " is-active" : ""}`}>
                    v{version.version}
                  </span>
                  {active && showActiveBadge && (
                    <span className="tc-chip tc-chip--active">Active</span>
                  )}
                </div>
                <span className="ce-dialog-subtitle">
                  Script preview · {formatDuration(totalDuration)} · {(version.blocks ?? []).length} {(version.blocks ?? []).length === 1 ? "block" : "blocks"}
                </span>
              </div>
            </div>
            <IconButton
              onClick={() => setOpen(false)}
              className="ce-dialog-close"
              aria-label="Close dialog"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </div>

          <div className="tc-dialog-legend">
            <div className="sd-legend-item">
              <span className="sd-legend-badge sd-legend-badge-t">T</span>
              Title — on-screen heading
            </div>
            <div className="sd-legend-item">
              <span className="sd-legend-badge sd-legend-badge-n">N</span>
              Narration — AI voiceover
            </div>
            <div className="sd-legend-item">
              <span className="sd-legend-badge sd-legend-badge-c">C</span>
              Caption — text overlay
            </div>
          </div>
        </div>

        <DialogContent className="tc-dialog-body ce-dialog-body" sx={{ overflowY: "auto" }}>
          <VersionBlocks version={version} highlighted={highlighted} active={active} showActiveBadge={showActiveBadge} />
        </DialogContent>

        <div className="ce-dialog-footer tc-dialog-footer">
          <div className="tc-dialog-footer-main">
            <div className="tc-dialog-actions">
              <button
                type="button"
                className="cv-btn cv-btn-primary"
                onClick={() => { setOpen(false); onRefine?.(version); }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 15 }} /> Refine
              </button>
              {multipleVersions && !active && (
                <button
                  type="button"
                  className="cv-btn cv-btn-secondary"
                  onClick={() => { setOpen(false); onActivate?.(version); }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 15 }} /> Mark Active
                </button>
              )}
              <button
                type="button"
                className="tc-delete-btn"
                onClick={() => { setOpen(false); onDelete?.(version); }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 15 }} /> Delete
              </button>
            </div>
            <button
              type="button"
              onClick={handleTurnToVideo}
              className="tc-dialog-video-cta"
            >
              <SmartDisplayOutlinedIcon sx={{ fontSize: 18 }} />
              <span>
                <strong>Turn into video</strong>
                <small>Start a quick video from this script</small>
              </span>
              <span className="tc-dialog-video-arrow" aria-hidden>→</span>
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

function ContentCard({ content, topicTitle, index, defaultOpenContentId, onActivate, onDelete, onRefine }: {
  content: ContentItem;
  topicTitle: string;
  index: number;
  defaultOpenContentId?: number | null;
  onActivate: (version: Version) => void;
  onDelete: (version: Version) => void;
  onRefine: (version: Version) => void;
}) {
  const formatIcon = getContentFormatIcon(content.content_format?.slug);
  const FormatIcon = formatIcon;
  const multipleVersions = (content.versions?.length ?? 0) > 1;
  const [expanded, setExpanded] = useState(true);

  return (
    <div
      className="tc-script-card"
      style={{ animationDelay: `${index * 0.06}s` }}
    >
      <div
        className="tc-script-header"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <ExpandMoreIcon
          className={`tc-expand-icon${expanded ? "" : " is-collapsed"}`}
        />
        <div className="tc-format-icon">
          <FormatIcon sx={{ fontSize: 17 }} />
        </div>
        <h3 className="tc-script-title">Script #{index + 1}</h3>
        <div className="tc-script-badges">
          {content.video_created && (
            <Tooltip title="Ready to create video">
              <span className="tc-video-icon">
                <VideocamIcon sx={{ fontSize: 15 }} />
              </span>
            </Tooltip>
          )}
          {content.tag_display && (
            <span className="tc-chip tc-chip--muted">{content.tag_display}</span>
          )}
          {(content.versions?.length ?? 0) > 1 && (
            <span className="tc-chip tc-chip--muted">
              {content.versions.length} {content.versions.length === 1 ? "version" : "versions"}
            </span>
          )}
        </div>
      </div>

      <Collapse in={expanded}>
        <div className="tc-versions">
          {(content.versions ?? []).map((version) => (
            <VersionBlock
              key={version.id}
              version={version}
              topicTitle={topicTitle}
              contentId={content.id}
              contentFormat={content.content_format}
              active={content.latest_version?.id === version.id}
              showActiveBadge={multipleVersions}
              showActiveHighlight={multipleVersions}
              multipleVersions={multipleVersions}
              defaultOpen={defaultOpenContentId === content.id && version.id === (content.latest_version?.id ?? content.versions?.[0]?.id)}
            onActivate={onActivate}
            onDelete={onDelete}
            onRefine={onRefine}
          />
        ))}
        </div>
      </Collapse>
    </div>
  );
}

const TopicContents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { topicId } = useParams<{ topicId: string }>();

  const openContentId = useMemo(
    () => (location.state as { openContent?: number } | null)?.openContent ?? null,
    [location.state]
  );

  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState<TopicDetail | null>(null);
  const [notFound, setNotFound] = useState(false);

  const loadTopic = useCallback(async () => {
    if (!topicId) return;
    setLoading(true);
    setNotFound(false);

    try {
      const res = await _get(`/content-engine/topics/${topicId}/`, {}, true);
      setTopic(res);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [topicId]);

  useEffect(() => {
    loadTopic();
  }, [loadTopic]);

  const handleActivate = async (version: Version) => {
    await _post(`/content-engine/versions/${version.id}/activate/`, {}, {}, false, true);
    loadTopic();
  };

  const handleDelete = async (version: Version) => {
    await _delete(`/content-engine/versions/${version.id}/`, {}, true);
    loadTopic();
  };

  const handleRefine = (version: Version) => {
    const content = topic?.contents.find((c) => c.versions.some((v) => v.id === version.id));
    if (!topic || !content) return;

    contentEngineService.setSelectedTopic(topic.id, topic.title);
    const scripts = topic.contents.map((c) =>
      c.id === content.id ? { ...c, latest_version: version } : c
    );
    const selected = scripts.find((s) => s.id === content.id);
    if (!selected) return;
    contentEngineService.setGeneratedContents(topic.id, scripts);
    contentEngineService.setSelectedScript(selected);
    navigate("/content-engine/script-detail");
  };

  const totalVersions = useMemo(
    () => (topic?.contents ?? []).reduce((sum, c) => sum + (c.versions?.length ?? 0), 0),
    [topic]
  );

  const distinctFormats = useMemo(() => {
    const seen = new Set<string>();
    return (topic?.contents ?? []).flatMap((c) => {
      if (!c.content_format || seen.has(c.content_format.slug)) return [];
      seen.add(c.content_format.slug);
      return [c.content_format];
    });
  }, [topic]);

  return (
    <div className="container-fluid library-page video-projects-page topic-contents-page">
      <MainHeading
        title={
          <span className="tc-title-formats">
            {topic ? capitalize(topic.title) : "Topic"}
            {distinctFormats.map((format) => (
              <span key={format.slug} className="tc-chip tc-chip--format">
                {format.name.toUpperCase()}
              </span>
            ))}
          </span>
        }
        subTitle={
          topic
            ? `${topic.contents?.length ?? 0} ${(topic.contents?.length ?? 0) === 1 ? "script" : "scripts"} · ${totalVersions} ${totalVersions === 1 ? "version" : "versions"}`
            : "Scripts and versions for this topic"
        }
      />

      <div className="tc-layout vp-layout">
        {!loading && topic && (
          <div className="tc-toolbar vp-toolbar">
            <div className="tc-meta">
              <ArticleOutlinedIcon sx={{ fontSize: 14 }} />
              <span>
                {topic.contents?.length ?? 0} {(topic.contents?.length ?? 0) === 1 ? "script" : "scripts"} · {totalVersions} {totalVersions === 1 ? "version" : "versions"}
              </span>
            </div>
            <div className="vp-toolbar-actions">
              <Tooltip title="Back to Library">
                <button
                  type="button"
                  className="cv-btn cv-btn-secondary lib-back-btn"
                  onClick={() => navigate("/library")}
                >
                  <ArrowBackIcon sx={{ fontSize: 18 }} />
                  Back to Library
                </button>
              </Tooltip>
            </div>
          </div>
        )}

        {loading ? (
          <PageSkeleton />
        ) : notFound || !topic ? (
          <div className="vp-empty">
            <div className="vp-empty-icon">
              <ArticleOutlinedIcon sx={{ fontSize: 32 }} />
            </div>
            <h3 className="vp-empty-title">Topic not found</h3>
            <p className="vp-empty-text">
              No scripts were found for this topic.
            </p>
            <button
              type="button"
              className="cv-btn cv-btn-secondary lib-back-btn"
              onClick={() => navigate("/library")}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
              Back to Library
            </button>
          </div>
        ) : topic.contents.length === 0 ? (
          <div className="vp-empty">
            <div className="vp-empty-icon">
              <ArticleOutlinedIcon sx={{ fontSize: 32 }} />
            </div>
            <h3 className="vp-empty-title">No scripts yet</h3>
            <p className="vp-empty-text">
              No scripts found for this topic.
            </p>
          </div>
        ) : (
          <div className="tc-scripts">
            {topic.contents.map((content, index) => (
              <ContentCard
                key={content.id}
                content={content}
                topicTitle={topic.title}
                index={index}
                defaultOpenContentId={openContentId}
                onActivate={handleActivate}
                onDelete={handleDelete}
                onRefine={handleRefine}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        .topic-contents-page .sd-card-actions {
          justify-content: flex-start;
        }
        .topic-contents-page .sd-card-stats {
          margin-right: auto;
        }
      `}</style>
    </div>
  );
};

export default TopicContents;
